import './InternalSecondaryMobile.css';
// src/MaintenanceDivisionMargins.js
//
// Division Margins — monthly revenue / costs / profit per service
// division, from Jobber invoices. L3-only (CAT_SERVICE).
//
// Divisions (classified server-side via the backfilled "Job Category"
// custom field + maintenance keyword detection):
//   In-Suite Service    revenue, labour + material costs, profit
//   Mechanical Service  revenue, labour + material costs, profit
//   Projects            invoices ≥ $50k pre-tax
//   Maintenance         revenue only (In-Suite vs Mechanical split) —
//                       excluded from the service margin numbers
//
// A month is fetched in 4 date-range parts (the nested Jobber query is
// too expensive for one Heroku request) and merged client-side.
// Results are cached per month via the shared stale-while-revalidate
// localStorage cache, so flipping between months is instant.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  FaBoxes, FaCalendarAlt, FaChevronDown, FaExternalLinkAlt,
  FaHardHat, FaHome, FaShieldAlt, FaSort, FaSortDown, FaSortUp,
  FaSyncAlt, FaWrench,
} from 'react-icons/fa';
import API_BASE_URL from './config/api';
import { cacheGet, cachePut, cacheAgeMs, formatAge } from './utils/staleCache';

const C = {
  navy: '#1F3A68',
  ink: '#1a2236',
  inkSoft: '#374151',
  muted: '#6b7280',
  line: '#E6E8EC',
  lineSoft: '#EFF2F7',
  rowLine: '#D9E0EA',
  bg: '#F4F7FB',
  card: '#ffffff',
  green: '#1c9550',
  greenSoft: '#E3F5EA',
  red: '#B62525',
  redSoft: '#FBE2E2',
  amber: '#b45309',
  amberSoft: '#FEF3C7',
  blue: '#2563eb',
  blueSoft: '#eff6ff',
  shadow: '0 1px 3px rgba(20,30,60,.06), 0 8px 24px rgba(20,30,60,.04)',
};

const sharedFont =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", "Helvetica Neue", Arial, sans-serif';

const PARTS = 4; // month is fetched in 4 date chunks, merged here
const FIRST_MONTH = '2026-01'; // Jobber epoch — no invoices before this

const fmtMoney = (n, dec = 0) =>
  '$' + Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: dec, maximumFractionDigits: dec });

const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

function monthOptions() {
  const out = [];
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  for (;;) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    out.push(key);
    if (key === FIRST_MONTH) break;
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
    if (out.length > 60) break; // safety
  }
  return out;
}

function monthLabel(key) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
}

// ── Week helpers (weeks are Mon–Sun, keyed by the Monday's date) ───
function localIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function addDays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return localIso(dt);
}

function weekOptions() {
  const now = new Date();
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
  const out = [];
  let cur = localIso(monday);
  while (cur >= `${FIRST_MONTH}-01` && out.length < 80) {
    out.push(cur);
    cur = addDays(cur, -7);
  }
  return out;
}

function weekLabel(mondayIso) {
  const fmt = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };
  return `Week of ${fmt(mondayIso)} – ${fmt(addDays(mondayIso, 6))}`;
}

// Merge one part's payload into the accumulating month result.
function mergePart(acc, part) {
  if (!acc) {
    return {
      divisions: JSON.parse(JSON.stringify(part.divisions)),
      lineItems: part.lineItems.map((x) => ({ ...x })),
      invoiceCount: part.invoiceCount,
      voidedCount: part.voidedCount,
      unlinkedCount: part.unlinkedCount,
      labourEstimatedCount: part.labourEstimatedCount || 0,
    };
  }
  for (const k of Object.keys(part.divisions)) {
    const d = acc.divisions[k];
    const p = part.divisions[k];
    for (const f of ['revenue', 'labourCost', 'materialCost', 'labourEstCost', 'materialEstCost',
                     'totalCost', 'profit', 'trackedProfit', 'invoiceCount']) {
      d[f] += p[f] || 0;
    }
    d.invoices = d.invoices.concat(p.invoices);
  }
  const byName = new Map(acc.lineItems.map((x) => [x.name.toLowerCase(), x]));
  for (const it of part.lineItems) {
    const k = it.name.toLowerCase();
    const cur = byName.get(k);
    if (cur) {
      for (const f of ['quantity', 'revenue', 'cost', 'costKnownQuantity', 'labourCost', 'invoiceCount']) {
        cur[f] += it[f];
      }
    } else {
      const copy = { ...it };
      byName.set(k, copy);
      acc.lineItems.push(copy);
    }
  }
  acc.invoiceCount += part.invoiceCount;
  acc.voidedCount += part.voidedCount;
  acc.unlinkedCount += part.unlinkedCount;
  acc.labourEstimatedCount += part.labourEstimatedCount || 0;
  return acc;
}

// ── Period dropdown (matches the tracker's date dropdown feel) ─────
// `options`: array of keys; `labelFor`: key → display string.
function PeriodDropdown({ value, options, labelFor, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => !disabled && setOpen((o) => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, minWidth: 210,
          padding: '10px 14px', borderRadius: 10, border: `1px solid ${C.line}`,
          background: C.card, cursor: disabled ? 'default' : 'pointer',
          fontFamily: sharedFont, fontSize: 15, fontWeight: 600, color: C.ink,
          boxShadow: C.shadow, opacity: disabled ? 0.6 : 1,
        }}
      >
        <FaCalendarAlt style={{ color: C.navy, fontSize: 14 }} />
        <span style={{ flex: 1, textAlign: 'left' }}>{labelFor(value)}</span>
        <FaChevronDown style={{ color: C.muted, fontSize: 12, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 40,
          background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
          boxShadow: '0 12px 32px rgba(20,30,60,.14)', minWidth: 230,
          maxHeight: 380, overflowY: 'auto', padding: 6,
        }}>
          {options.map((m) => (
            <div
              key={m}
              onClick={() => { onChange(m); setOpen(false); }}
              style={{
                padding: '11px 14px', borderRadius: 8, cursor: 'pointer',
                fontSize: 15, fontWeight: m === value ? 700 : 500,
                color: m === value ? C.navy : C.inkSoft,
                background: m === value ? C.blueSoft : 'transparent',
              }}
              onMouseEnter={(e) => { if (m !== value) e.currentTarget.style.background = C.lineSoft; }}
              onMouseLeave={(e) => { if (m !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              {labelFor(m)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Division card ─────────────────────────────────────────────────
function StatRow({ label, value, color, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0' }}>
      <span style={{ fontSize: 13.5, color: C.muted }}>{label}</span>
      <span style={{ fontSize: bold ? 17 : 14.5, fontWeight: bold ? 800 : 600, color: color || C.ink, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

function InvoiceList({ invoices, showCosts }) {
  return (
    <div style={{ maxHeight: 340, overflowY: 'auto', marginTop: 8, border: `1px solid ${C.lineSoft}`, borderRadius: 10 }}>
      {invoices.map((inv, i) => (
        <div
          className="division-invoice-row"
          key={`${inv.invoiceNumber}-${i}`}
          style={{
            display: 'grid',
            gridTemplateColumns: showCosts ? 'minmax(0,1fr) 78px 92px 92px' : 'minmax(0,1fr) 78px 92px',
            gap: 10, alignItems: 'center', padding: '9px 12px',
            borderTop: i === 0 ? 'none' : `1px solid ${C.rowLine}`,
            fontSize: 13.5,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {inv.url ? (
                <a href={inv.url} target="_blank" rel="noreferrer" style={{ color: C.ink, textDecoration: 'none' }}>
                  {inv.client} <FaExternalLinkAlt style={{ fontSize: 9, color: C.muted, marginLeft: 3 }} />
                </a>
              ) : inv.client}
            </div>
            <div style={{ color: C.muted, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              #{inv.invoiceNumber}{inv.jobTitle ? ` · ${inv.jobTitle}` : ''}{inv.linkedJob ? '' : ' · no linked job'}
            </div>
          </div>
          <div style={{ color: C.muted, fontSize: 12.5, whiteSpace: 'nowrap' }}>{inv.issuedDate}</div>
          <div style={{ textAlign: 'right', fontWeight: 700, color: C.ink, fontVariantNumeric: 'tabular-nums' }}>
            {fmtMoney(inv.revenue)}
          </div>
          {showCosts && (
            <div
              title={`Estimated labour ${fmtMoney(inv.labourEst ?? 0)}, material ${fmtMoney(inv.materialEst ?? 0)}. Jobber-stored profit: ${fmtMoney(inv.trackedProfit ?? inv.profit)}`}
              style={{ textAlign: 'right', fontWeight: 700, color: inv.profit >= 0 ? C.green : C.red, fontVariantNumeric: 'tabular-nums' }}
            >
              {fmtMoney(inv.profit)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DivisionCard({ title, icon, accent, data, showCosts = true, note, checkMaterialCoverage = false }) {
  const [open, setOpen] = useState(false);
  const margin = data.revenue > 0 ? (data.profit / data.revenue) * 100 : 0;
  // Mechanical jobs rarely have material costs we can estimate (their
  // custom line items aren't in the price book and expenses are seldom
  // logged) — when under half the invoices have any material figure,
  // show "?" and flag the profit as approximate.
  const matTracked = data.invoices.filter((i) => (i.materialEst ?? i.materialCost) > 0).length;
  const materialUnknown =
    checkMaterialCoverage && data.invoices.length > 0 && matTracked / data.invoices.length < 0.5;
  return (
    <div style={{ background: C.card, borderRadius: 14, boxShadow: C.shadow, border: `1px solid ${C.line}`, padding: '18px 20px', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{
          width: 34, height: 34, borderRadius: 9, display: 'inline-flex', alignItems: 'center',
          justifyContent: 'center', background: accent.soft, color: accent.main, fontSize: 15,
        }}>
          {icon}
        </span>
        <span style={{ fontSize: 16.5, fontWeight: 800, color: C.ink }}>{title}</span>
      </div>

      <div style={{ fontSize: 28, fontWeight: 800, color: C.ink, fontVariantNumeric: 'tabular-nums', margin: '2px 0 2px' }}>
        {fmtMoney(data.revenue)}
        <span style={{ fontSize: 13, fontWeight: 600, color: C.muted, marginLeft: 7 }}>revenue</span>
      </div>

      {showCosts ? (
        <div
          style={{ borderTop: `1px solid ${C.lineSoft}`, marginTop: 8, paddingTop: 6 }}
          title={`Jobber-stored numbers for comparison — labour tracked on timesheets: ${fmtMoney(data.labourCost)}, materials/expenses recorded: ${fmtMoney(data.materialCost)}, profit on stored data: ${fmtMoney(data.trackedProfit)}`}
        >
          <StatRow label="Labour (est. hours × $50)" value={fmtMoney(data.labourEstCost)} color={C.inkSoft} />
          {materialUnknown ? (
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '5px 0', cursor: 'help' }}
            >
              <span style={{ fontSize: 13.5, color: C.muted }}>Material (price book + expenses)</span>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: C.amber }}>
                ?{' '}
                <span style={{ fontSize: 11.5, fontWeight: 500, color: C.muted }}>
                  ({matTracked}/{data.invoices.length} known)
                </span>
              </span>
            </div>
          ) : (
            <StatRow label="Material (price book + expenses)" value={fmtMoney(data.materialEstCost)} color={C.inkSoft} />
          )}
          <StatRow
            label={materialUnknown ? 'Profit (pre-material)' : 'Profit'}
            value={`${materialUnknown ? '~' : ''}${fmtMoney(data.profit)}  (${fmtPct(margin)})`}
            color={data.profit >= 0 ? C.green : C.red}
            bold
          />
        </div>
      ) : (
        note && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>{note}</div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        disabled={!data.invoices.length}
        style={{
          marginTop: 10, alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', padding: 0, cursor: data.invoices.length ? 'pointer' : 'default',
          fontFamily: sharedFont, fontSize: 13, fontWeight: 700, color: data.invoices.length ? C.blue : C.muted,
        }}
      >
        {data.invoiceCount} invoice{data.invoiceCount === 1 ? '' : 's'}
        {data.invoices.length > 0 && (
          <FaChevronDown style={{ fontSize: 10, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        )}
      </button>
      {open && <InvoiceList invoices={data.invoices} showCosts={showCosts} />}
    </div>
  );
}

// ── Line-item profitability table ─────────────────────────────────
// Labour hours per unit — fixed estimates ($50/h). Specific items
// first, then time-billed items (anything with "hour" in the name
// counts as 1h of our labour), then price tiers.
function estimateHours(name, unitPrice) {
  const n = name.toLowerCase();
  if (/mold|mould/.test(n)) return 8;
  if (/blower motor/.test(n)) return 4;
  if (/hose/.test(n)) return 2;
  if (/leak sensor/.test(n)) return 1;
  if (/hour/.test(n)) return 1;
  return unitPrice >= 1000 ? 8 : 3;
}

function LineItemTable({ items }) {
  const [sortKey, setSortKey] = useState('revenue');
  const [sortDir, setSortDir] = useState('desc');

  const rows = useMemo(() => {
    const enriched = items.map((it) => {
      const costKnown = it.costKnownQuantity >= it.quantity - 0.01 && it.quantity > 0;
      // Labour is a FIXED estimate (timesheet costing is too spotty to
      // trust per line item): $50/h × estimateHours() per unit.
      const unitPrice = it.quantity > 0 ? it.revenue / it.quantity : 0;
      const estHours = estimateHours(it.name, unitPrice);
      const labourPerUnit = estHours * 50;
      const labour = labourPerUnit * it.quantity;
      const totalCost = (costKnown ? it.cost : 0) + labour;
      const profit = it.revenue - totalCost;
      const margin = it.revenue > 0 ? (profit / it.revenue) * 100 : null;
      const avgCost = it.quantity > 0 ? totalCost / it.quantity : null;
      // Per-unit material cost — i.e. the raw price-book unit cost.
      const matPerUnit = costKnown && it.quantity > 0 ? it.cost / it.quantity : null;
      const profitPerUnit = it.quantity > 0 ? profit / it.quantity : null;
      return { ...it, costKnown, labour, avgCost, matPerUnit, labourPerUnit, estHours, profit, profitPerUnit, margin };
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    enriched.sort((a, b) => {
      let av = a[sortKey]; let bv = b[sortKey];
      if (sortKey === 'name') return dir * a.name.localeCompare(b.name);
      if (av == null) av = -Infinity;
      if (bv == null) bv = -Infinity;
      return dir * (av - bv);
    });
    return enriched;
  }, [items, sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };

  const SortIcon = ({ col }) => {
    if (col !== sortKey) return <FaSort style={{ fontSize: 10, color: '#c3cbd8', marginLeft: 4 }} />;
    return sortDir === 'asc'
      ? <FaSortUp style={{ fontSize: 10, color: C.navy, marginLeft: 4 }} />
      : <FaSortDown style={{ fontSize: 10, color: C.navy, marginLeft: 4 }} />;
  };

  const th = (label, key, align = 'right') => (
    <th
      onClick={() => onSort(key)}
      style={{
        textAlign: align, padding: '10px 12px', fontSize: 12.5, fontWeight: 700,
        color: C.muted, textTransform: 'uppercase', letterSpacing: 0.4,
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        borderBottom: `1px solid ${C.rowLine}`,
      }}
    >
      {label}<SortIcon col={key} />
    </th>
  );

  const td = { padding: '11px 12px', fontSize: 14, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

  return (
    <div className="internal-data-scroll" role="region" aria-label="Scrollable data table" tabIndex={0} style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
        <thead>
          <tr>
            {th('Line item', 'name', 'left')}
            {th('Qty', 'quantity')}
            {th('Revenue', 'revenue')}
            {th('Profit', 'profit')}
            {th('Mat. cost / unit', 'matPerUnit')}
            {th('Labour / unit', 'labourPerUnit')}
            {th('Avg cost / unit', 'avgCost')}
            {th('Profit / unit', 'profitPerUnit')}
            {th('Margin', 'margin')}
          </tr>
        </thead>
        <tbody>
          {rows.map((it, i) => (
            <tr key={it.name} style={{ borderTop: i === 0 ? 'none' : `1px solid ${C.rowLine}` }}>
              <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: C.ink, whiteSpace: 'normal', maxWidth: 380 }}>
                {it.name}
              </td>
              <td style={{ ...td, textAlign: 'right', color: C.inkSoft }}>{Number(it.quantity.toFixed(1))}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: C.ink }}>{fmtMoney(it.revenue)}</td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: it.profit >= 0 ? C.green : C.red }}>
                {fmtMoney(it.profit)}
              </td>
              <td style={{ ...td, textAlign: 'right', color: C.inkSoft }}>
                {it.matPerUnit == null ? <span style={{ color: '#9aa4b2' }}>n/a</span> : fmtMoney(it.matPerUnit)}
              </td>
              <td style={{ ...td, textAlign: 'right', color: C.inkSoft }}>
                {fmtMoney(it.labourPerUnit)}
                <div style={{ fontSize: 11.5, color: '#9aa4b2', fontWeight: 500 }}>{it.estHours}h × $50</div>
              </td>
              <td style={{ ...td, textAlign: 'right', color: C.inkSoft }}>
                {it.avgCost == null ? <span style={{ color: '#9aa4b2' }}>—</span> : fmtMoney(it.avgCost)}
                {!it.costKnown && (
                  <div style={{ fontSize: 11.5, color: '#9aa4b2', fontWeight: 500 }}>labour only</div>
                )}
              </td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: it.profitPerUnit == null ? '#9aa4b2' : it.profitPerUnit >= 0 ? C.green : C.red }}>
                {it.profitPerUnit == null ? '—' : fmtMoney(it.profitPerUnit)}
              </td>
              <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: it.margin == null ? '#9aa4b2' : it.margin >= 0 ? C.green : C.red }}>
                {it.margin == null ? '—' : fmtPct(it.margin)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────
export default function MaintenanceDivisionMargins() {
  const [searchParams, setSearchParams] = useSearchParams();
  const monthOpts = useMemo(monthOptions, []);
  const weekOpts = useMemo(weekOptions, []);

  const mode = searchParams.get('mode') === 'week' ? 'week' : 'month';
  const month = /^\d{4}-\d{2}$/.test(searchParams.get('month') || '') ? searchParams.get('month') : monthOpts[0];
  const week = weekOpts.includes(searchParams.get('week') || '') ? searchParams.get('week') : weekOpts[0];
  // One string that fully identifies the selected period; also the
  // cache key suffix.
  const periodKey = mode === 'week' ? `week:${week}` : month;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadPart, setLoadPart] = useState(0);
  const [loadParts, setLoadParts] = useState(PARTS);
  const [error, setError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const abortRef = useRef({ token: 0 });

  const setParam = (updates) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(updates)) next.set(k, v);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async (key, { fresh = false } = {}) => {
    // v6: division cards use estimated labour/material (fixed hour
    // rules + price book), tracked numbers kept for tooltips.
    const cacheKey = `division-margins:v6:${key}`;
    if (!fresh) {
      const hit = cacheGet(cacheKey);
      if (hit && hit.value) {
        setData(hit.value);
        setFetchedAt(Date.now() - cacheAgeMs(hit));
        setError(null);
        setLoading(false);
        return;
      }
    }

    // A week fits in one request; a month needs PARTS chunks.
    const isWeek = key.startsWith('week:');
    const urls = isWeek
      ? [`${API_BASE_URL}/api/jobber/division-margins?from=${key.slice(5)}&to=${addDays(key.slice(5), 6)}`]
      : Array.from({ length: PARTS }, (_, i) =>
          `${API_BASE_URL}/api/jobber/division-margins?month=${key}&part=${i + 1}&parts=${PARTS}`);

    const myToken = ++abortRef.current.token;
    setLoading(true);
    setError(null);
    setLoadPart(1);
    setLoadParts(urls.length);

    try {
      const token = localStorage.getItem('managerToken') || '';
      let merged = null;
      for (let i = 0; i < urls.length; i++) {
        if (abortRef.current.token !== myToken) return; // superseded
        setLoadPart(i + 1);
        let body = null;
        let lastErr = null;
        // A part can transiently 502/timeout (Jobber throttling) —
        // retry it a couple of times before failing the whole load.
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const resp = await fetch(urls[i], { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            body = await resp.json().catch(() => ({}));
            if (!resp.ok || body.error) throw new Error(body.error || `HTTP ${resp.status}`);
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
          }
        }
        if (lastErr) throw lastErr;
        merged = mergePart(merged, body);
      }
      if (abortRef.current.token !== myToken) return;
      merged.lineItems.sort((a, b) => b.revenue - a.revenue);
      for (const k of Object.keys(merged.divisions)) {
        merged.divisions[k].invoices.sort((a, b) => b.revenue - a.revenue);
      }
      setData(merged);
      setFetchedAt(Date.now());
      cachePut(cacheKey, merged);
    } catch (e) {
      if (abortRef.current.token === myToken) setError(e.message || 'Failed to load');
    } finally {
      if (abortRef.current.token === myToken) { setLoading(false); setLoadPart(0); }
    }
  }, []);

  useEffect(() => { load(periodKey); }, [periodKey, load]);

  const div = data?.divisions;
  const serviceTotals = useMemo(() => {
    if (!div) return null;
    const rev = div.insuite.revenue + div.mechanical.revenue + div.projects.revenue;
    const profit = div.insuite.profit + div.mechanical.profit + div.projects.profit;
    return { rev, profit };
  }, [div]);

  return (
    <div className="internal-secondary-page internal-margins-page" style={{ fontFamily: sharedFont, background: C.bg, minHeight: '100%', padding: '26px 28px 48px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
        <div className="internal-flex-heading" style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.ink }}>Division Margins</h1>
          <div style={{ fontSize: 13.5, color: C.muted, marginTop: 3 }}>
            Revenue from Jobber invoices (pre-tax); costs are estimates — labour at fixed hours × $50/h, materials from the price book.
            {fetchedAt && !loading && <span> · Updated {formatAge(Date.now() - fetchedAt)}</span>}
          </div>
        </div>
        {/* Month / Week mode toggle */}
        <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.line}`, boxShadow: C.shadow }}>
          {['month', 'week'].map((m) => (
            <button
              key={m}
              onClick={() => !loading && setParam({ mode: m })}
              style={{
                padding: '10px 16px', border: 'none', cursor: loading ? 'default' : 'pointer',
                fontFamily: sharedFont, fontSize: 14, fontWeight: 700,
                background: mode === m ? C.navy : C.card,
                color: mode === m ? '#fff' : C.inkSoft,
              }}
            >
              {m === 'month' ? 'Month' : 'Week'}
            </button>
          ))}
        </div>
        {mode === 'week' ? (
          <PeriodDropdown
            value={week}
            options={weekOpts}
            labelFor={weekLabel}
            onChange={(w) => setParam({ mode: 'week', week: w })}
            disabled={loading}
          />
        ) : (
          <PeriodDropdown
            value={month}
            options={monthOpts}
            labelFor={monthLabel}
            onChange={(m) => setParam({ mode: 'month', month: m })}
            disabled={loading}
          />
        )}
        <button
          onClick={() => load(periodKey, { fresh: true })}
          disabled={loading}
          title="Refresh from Jobber"
          style={{
            width: 42, height: 42, borderRadius: 10, border: `1px solid ${C.line}`,
            background: loading ? C.blueSoft : C.card, color: C.navy, cursor: loading ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxShadow: C.shadow,
          }}
        >
          <FaSyncAlt style={{ fontSize: 15, animation: loading ? 'dmspin 1s linear infinite' : 'none' }} />
        </button>
      </div>
      <style>{'@keyframes dmspin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }'}</style>

      {loading && (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, boxShadow: C.shadow, padding: '38px 20px', textAlign: 'center', marginTop: 18 }}>
          <div style={{ fontSize: 15.5, fontWeight: 700, color: C.ink }}>
            Loading {mode === 'week' ? weekLabel(week) : monthLabel(month)} from Jobber…
          </div>
          <div style={{ fontSize: 13.5, color: C.muted, marginTop: 6 }}>
            {loadParts > 1
              ? `Part ${loadPart} of ${loadParts} — a full month takes ~20 seconds.`
              : 'A week loads in a few seconds.'}
          </div>
          <div style={{ width: 260, height: 8, borderRadius: 6, background: C.lineSoft, margin: '16px auto 0', overflow: 'hidden' }}>
            <div style={{ width: `${(loadPart / loadParts) * 100}%`, height: '100%', borderRadius: 6, background: C.navy, transition: 'width .4s' }} />
          </div>
        </div>
      )}

      {error && !loading && (
        <div style={{ background: C.redSoft, border: `1px solid #f2c4c4`, color: C.red, borderRadius: 12, padding: '14px 18px', fontSize: 14.5, fontWeight: 600, marginTop: 18 }}>
          Couldn't load data from Jobber: {error}
          <button
            onClick={() => load(periodKey, { fresh: true })}
            style={{ marginLeft: 12, border: 'none', background: C.red, color: '#fff', borderRadius: 8, padding: '6px 14px', fontWeight: 700, cursor: 'pointer', fontFamily: sharedFont }}
          >
            Retry
          </button>
        </div>
      )}

      {data && !loading && !error && (
        <>
          {/* Summary strip */}
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', margin: '14px 0 18px', fontSize: 13.5, color: C.muted }}>
            <span><b style={{ color: C.ink }}>{data.invoiceCount}</b> invoices</span>
            {serviceTotals && (
              <span>
                Service total: <b style={{ color: C.ink }}>{fmtMoney(serviceTotals.rev)}</b> revenue,{' '}
                <b style={{ color: serviceTotals.profit >= 0 ? C.green : C.red }}>{fmtMoney(serviceTotals.profit)}</b> profit
              </span>
            )}
            {data.voidedCount > 0 && <span>{data.voidedCount} voided excluded</span>}
            {data.unlinkedCount > 0 && (
              <span title="Invoices with no linked Jobber job (client-portal one-offs) — counted as In-Suite">
                {data.unlinkedCount} with no linked job → In-Suite
              </span>
            )}
            <span title="Costs are modeled: labour = fixed hours per line item × $50/h; material = price-book unit costs + expenses logged on the job. Hover any card or invoice for Jobber's stored numbers.">
              costs are estimates
            </span>
          </div>

          {/* Division cards */}
          <div className="internal-mobile-stack" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(310px, 1fr))', gap: 18 }}>
            <DivisionCard
              title="In-Suite Service"
              icon={<FaHome />}
              accent={{ main: C.amber, soft: C.amberSoft }}
              data={div.insuite}
            />
            <DivisionCard
              title="Mechanical Service"
              icon={<FaWrench />}
              accent={{ main: C.green, soft: C.greenSoft }}
              data={div.mechanical}
              checkMaterialCoverage
            />
            <DivisionCard
              title="Projects"
              icon={<FaHardHat />}
              accent={{ main: C.blue, soft: C.blueSoft }}
              data={div.projects}
            />
          </div>

          {/* Maintenance revenue (no costs) */}
          <div style={{ margin: '26px 0 10px', display: 'flex', alignItems: 'center', gap: 9 }}>
            <FaShieldAlt style={{ color: C.navy, fontSize: 15 }} />
            <span style={{ fontSize: 17.5, fontWeight: 800, color: C.ink }}>Maintenance Revenue</span>
            <span style={{ fontSize: 13, color: C.muted }}>— contract billing, excluded from the service margins above</span>
          </div>
          <div className="internal-mobile-stack" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(310px, 1fr))', gap: 18 }}>
            <DivisionCard
              title="In-Suite Maintenance"
              icon={<FaHome />}
              accent={{ main: C.navy, soft: '#E8EEF9' }}
              data={div.maintenanceInsuite}
              showCosts={false}
              note="Revenue only — costs not tracked for maintenance contracts."
            />
            <DivisionCard
              title="Mechanical Maintenance"
              icon={<FaWrench />}
              accent={{ main: C.navy, soft: '#E8EEF9' }}
              data={div.maintenanceMech}
              showCosts={false}
              note="Revenue only — costs not tracked for maintenance contracts."
            />
          </div>

          {/* Line item profitability */}
          <div style={{ background: C.card, borderRadius: 14, boxShadow: C.shadow, border: `1px solid ${C.line}`, padding: '18px 20px', marginTop: 26 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 2 }}>
              <FaBoxes style={{ color: C.navy, fontSize: 15 }} />
              <span style={{ fontSize: 17.5, fontWeight: 800, color: C.ink }}>Line Item Profitability</span>
            </div>
            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 12 }}>
              Saved price-book items on service invoices (one-off custom lines and maintenance billing
              excluded). Labour is a fixed estimate at $50/h: mold 8h, blower motor 4h, hose 2h, leak
              sensor and hourly-billed items 1h, otherwise 3h under $1,000 and 8h for $1,000+. Profit =
              revenue − material cost (price book) − estimated labour; “n/a” = no material cost on file.
            </div>
            <LineItemTable items={data.lineItems} />
          </div>
        </>
      )}
    </div>
  );
}
